import { SweepCanvas } from '../sweep-canvas/SweepCanvas';
import { NodeEntityTypes, OnNodeClickProps } from '../sweep-canvas/canvas-types';
import { useFunnelMapElements } from './useFunnelMapElements';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { FunnelMapCanvasInternalCtx } from './FunnelMapCanvasInternalCtx';
import { FunnelMapCanvasProps } from './types';
import { useSfFunnelsEventHandler } from './calculate-events/useSfFunnelsEventHandler';
import { useThirdPartyEventHandler } from './calculate-events/useThirdParyEventHandler';
import { useMetadataElementEventHandler } from './calculate-events/useMetadataElementEventHandler';
import { getFunnelTypeFromNodeId } from './helper';
import { FunnelType } from '../../types/enums/FunnelType';
import { useFunnelMapCanvasCtx } from './FunnelMapCanvasCtx';
import {
  OnConnectClick,
  OnEdgeDeleteClick,
} from '../sweep-canvas/internal-context/SweepCanvasPropsCtx';
import { useStageMetadata } from '../../hooks/useStageMetadata';
import { getFullMetadataElementId } from '../pages/funnel-map-flow/metadata-elements/metadataElementUtils';
import usePrevious from '../../hooks/usePrevious';
import { useSweepCanvasFitView } from '../sweep-canvas/useSweepCanvasFitView';
import { uniqueId } from 'lodash';

const EMPTY_OBJECT = {};

const FunnelMapCanvas = () => {
  const {
    funnelMap,
    readonly,
    visibilityMap,
    hideGroupInfo,
    onRemoveFunnelLinkClick,
    onFunnelOverlayClick,
    onPluginClick,
    selectedGateId,
    holdNodeHighlighted,
    selectedStageId,
    isLoadingCursor,
    transientFunnel,
    onFunnelLabelClick,
    onRecordTypeLabelClick,
    onFunnelLabelPillClick,
    onRecordTypeLabelPillClick,
    renderZeroCountPills,
    orgPillsMap,
    canvasNodesMouseMove,
    autoFitViewOnFirstNodes,
    onSweepElementsChange,
    onConnectFunnelOrStepsClick,
    placingTemplate,
    controlsRightMargin,
    disableNodeHighlight,
    objectTypesByName,
    stagesWithSparkleIcon,
    selectedFunnelId,
    crmOrgs,
    zoomToFitOnFirstNodes,
    funnelStatusMap,
    sfFunnelMoreButton,
    disableMultiSelection,
    onSectionDeleteClick,
  } = useContext(FunnelMapCanvasInternalCtx);

  const isPlacingPlugin = Boolean(onFunnelOverlayClick);

  const funnelsData: FunnelsData = funnelMap.funnelsData || EMPTY_OBJECT;
  const recordTypesData: RecordTypesData = funnelMap.recordTypesData || EMPTY_OBJECT;

  const { newFunnelMap } = useMemo(() => {
    let funnelMapWithTransientFunnel = cloneDeep(funnelMap);

    let sfFunnelsDataWithTransientFunnel = cloneDeep(funnelsData);
    if (
      transientFunnel?.type === FunnelType.SALESFORCE &&
      transientFunnel?.importType == 'import'
    ) {
      const _transientFunnel = transientFunnel.data.funnel;
      funnelMapWithTransientFunnel = { ...funnelMapWithTransientFunnel };
      sfFunnelsDataWithTransientFunnel = { ...sfFunnelsDataWithTransientFunnel };

      funnelMapWithTransientFunnel.funnels[_transientFunnel.id] = {
        position: {
          column: 0,
          row: 0,
        },
      };
      sfFunnelsDataWithTransientFunnel[_transientFunnel.id] = _transientFunnel;
    }
    if (
      transientFunnel?.type === FunnelType.THIRD_PARTY &&
      transientFunnel?.importType == 'import'
    ) {
      const _transientThirdPartyFunnel = transientFunnel.data.thirdPartyFunnel;
      funnelMapWithTransientFunnel = { ...funnelMapWithTransientFunnel };
      sfFunnelsDataWithTransientFunnel = { ...sfFunnelsDataWithTransientFunnel };

      funnelMapWithTransientFunnel.thirdPartyFunnels =
        funnelMapWithTransientFunnel.thirdPartyFunnels || {};
      funnelMapWithTransientFunnel.thirdPartyFunnelsData =
        funnelMapWithTransientFunnel.thirdPartyFunnelsData || {};

      funnelMapWithTransientFunnel.thirdPartyFunnels[_transientThirdPartyFunnel.id] = {
        position: {
          column: 0,
          row: 0,
        },
      };
      funnelMapWithTransientFunnel.thirdPartyFunnelsData[_transientThirdPartyFunnel.id] =
        _transientThirdPartyFunnel;
    }
    if (
      transientFunnel?.type === FunnelType.METADATA_ELEMENT &&
      transientFunnel?.importType == 'import'
    ) {
      if (!funnelMapWithTransientFunnel.metadataElements) {
        funnelMapWithTransientFunnel.metadataElements = {};
      }
      if (!funnelMapWithTransientFunnel.metadataElementsData) {
        funnelMapWithTransientFunnel.metadataElementsData = {};
      }
      const transientMetadataElements = transientFunnel.data.metadataElements;
      const connections = transientFunnel.data.connections;
      transientMetadataElements.forEach((transientMetadataElement) => {
        const fullMetadataId = getFullMetadataElementId(transientMetadataElement);
        funnelMapWithTransientFunnel = { ...funnelMapWithTransientFunnel };
        funnelMapWithTransientFunnel.metadataElementsData[fullMetadataId] =
          transientMetadataElement;

        funnelMapWithTransientFunnel.metadataElements[fullMetadataId] = {
          position: transientMetadataElement.position,
          funnelLinks: [],
          metadataId: transientMetadataElement.metadataId,
          crmOrgId: transientMetadataElement.crmOrgId,
          color: transientMetadataElement.color,
          icon: transientMetadataElement.icon,
        };
        if (connections) {
          connections.forEach((connection) => {
            if (funnelMapWithTransientFunnel.metadataElements[connection.source]) {
              funnelMapWithTransientFunnel.metadataElements[connection.source].funnelLinks.push({
                _id: uniqueId(),
                _type: connection.targetType,
                funnelId: connection.target,
              });
            }
          });
        }
      });
    }

    if (placingTemplate) {
      funnelMapWithTransientFunnel = { ...funnelMapWithTransientFunnel };
      sfFunnelsDataWithTransientFunnel = { ...sfFunnelsDataWithTransientFunnel };

      placingTemplate.template.funnelTemplates.forEach((template) => {
        funnelMapWithTransientFunnel.funnels[template.id] = {
          position: template.position,
        };
        sfFunnelsDataWithTransientFunnel[template.id] = {
          id: template.id,
          name: template.name,
          recordType: {
            description: '',
            label: '',
            name: template.objectName,
            objectName: template.objectName,
          },
          funnelDetails: template.templateFunnelDetails,
          description: '',
          accountId: '',
          createdAt: '',
          createdById: '',
          updatedAt: '',
          snapshotsIds: [],
          updatedById: '',
          apiName: '',
          crmOrgId: placingTemplate.crmOrgId,
        };
      });
    }

    const newFunnelMap: FunnelMap = {
      ...funnelMapWithTransientFunnel,
      funnelsData: sfFunnelsDataWithTransientFunnel,
      recordTypesData,
    };
    return {
      newFunnelMap,
    };
  }, [funnelMap, funnelsData, placingTemplate, recordTypesData, transientFunnel]);

  const { editingThirdPartyStepId, setEditingThirdPartyStepId } = useFunnelMapCanvasCtx();
  const { mapForecastCategoryValueToLabel } = useStageMetadata();

  const {
    sweepEdges,
    sweepGroups,
    sweepNodes,
    sweepStandaloneNodes,
    sweepSections,
    sweepStickyNotesNodes,
    sweepTextNodes,
  } = useFunnelMapElements({
    funnelMap: newFunnelMap,
    visibilityMap,
    transientFunnel,
    renderZeroCountPills,
    orgPillsMap,
    objectTypesByName,
    editingThirdPartyStepId,
    stagesWithSparkleIcon,
    mapForecastCategoryValueToLabel,
    crmOrgs,
    funnelStatusMap,
    sfFunnelMoreButton,
  });

  const sfFunnelEventsHandler = useSfFunnelsEventHandler();
  const thirdPartyEventHandler = useThirdPartyEventHandler({
    setEditingThirdPartyStepId,
  });
  const metadataElementEventsHandler = useMetadataElementEventHandler();

  const eventHandlerBaseOnNodeType = useCallback(
    (nodeType: FunnelType | undefined) => {
      switch (nodeType) {
        case FunnelType.SALESFORCE:
          return sfFunnelEventsHandler;
        case FunnelType.THIRD_PARTY:
          return thirdPartyEventHandler;
        case FunnelType.METADATA_ELEMENT:
          return metadataElementEventsHandler;

        default:
          return null;
      }
    },
    [sfFunnelEventsHandler, thirdPartyEventHandler, metadataElementEventsHandler],
  );

  const onNodeClick = useCallback(
    (props: OnNodeClickProps) => {
      if (props.entity.type === NodeEntityTypes.SectionDelete) {
        onSectionDeleteClick?.(props.id);
        return;
      }
      const eventHandler = eventHandlerBaseOnNodeType(
        getFunnelTypeFromNodeId(funnelMap, props.parentId ?? props.id),
      );
      if (eventHandler) {
        eventHandler.onNodeClick?.(props);
      }
    },

    [eventHandlerBaseOnNodeType, funnelMap, onSectionDeleteClick],
  );
  const onPillClick = useCallback(
    (props: OnNodeClickProps) =>
      eventHandlerBaseOnNodeType(
        getFunnelTypeFromNodeId(funnelMap, props.parentId ?? props.id),
      )?.onPillClick?.(props),
    [eventHandlerBaseOnNodeType, funnelMap],
  );

  const onLabelClick = useCallback(
    (props: OnNodeClickProps) => {
      const eventHandler = eventHandlerBaseOnNodeType(
        getFunnelTypeFromNodeId(funnelMap, props.parentId ?? props.id),
      );
      if (eventHandler) {
        eventHandler.onLabelClick?.(props);
      }
    },
    [eventHandlerBaseOnNodeType, funnelMap],
  );

  const onGateClick = sfFunnelEventsHandler.onGateClick;

  const onNodeNameChange = useCallback(
    (name: string, funnelId: string) =>
      eventHandlerBaseOnNodeType(getFunnelTypeFromNodeId(funnelMap, funnelId))?.onNodeNameChange?.(
        name,
        funnelId,
      ) ?? { isValid: true },
    [eventHandlerBaseOnNodeType, funnelMap],
  );

  const hasPillClick = !!(onFunnelLabelPillClick || onRecordTypeLabelPillClick);
  const hasLabelClick = !!(onFunnelLabelClick || onRecordTypeLabelClick);

  const _onConnectStepsClick: OnConnectClick = useCallback(
    (event, { stepId, groupId: funnelId }, parentEl) => {
      const funnelType = getFunnelTypeFromNodeId(funnelMap, funnelId);
      if (funnelType) {
        onConnectFunnelOrStepsClick?.(event, { stepId, funnelId, funnelType }, parentEl);
      }
    },
    [funnelMap, onConnectFunnelOrStepsClick],
  );

  const _onRemoveFunnelLinkClick: OnEdgeDeleteClick = useCallback(
    ({ edgeId: linkId, sourceNodeId, sourceNodeParentId, targetNodeId, targetNodeParentId }) => {
      const sourceFunnelId = sourceNodeParentId ?? sourceNodeId; // If the node doesn't have a parent it means it's a funnel
      const sourceStageId = sourceNodeParentId ? sourceNodeId : undefined;
      const targetFunnelId = targetNodeParentId ?? targetNodeId;
      const targetStageId = targetNodeParentId ? targetNodeId : undefined;

      const originFunnelType = getFunnelTypeFromNodeId(funnelMap, sourceFunnelId);

      if (originFunnelType) {
        onRemoveFunnelLinkClick?.({
          linkId,
          sourceStageId,
          targetStageId,
          sourceFunnelId,
          targetFunnelId,
          originFunnelType,
        });
      }
    },
    [funnelMap, onRemoveFunnelLinkClick],
  );

  const simpleHighlightedEntities = useMemo(() => {
    const entities = [];

    if (selectedStageId) {
      entities.push(selectedStageId);
    }
    if (selectedGateId) {
      entities.push(selectedGateId);
    }
    if (selectedFunnelId) {
      entities.push(selectedFunnelId);
    }
    return entities;
  }, [selectedFunnelId, selectedGateId, selectedStageId]);

  const nodesLength = sweepNodes.length + sweepStandaloneNodes.length;
  const previousLength = usePrevious(nodesLength);
  const { fitView } = useSweepCanvasFitView();

  useEffect(() => {
    if (previousLength === 0 && nodesLength > 0 && zoomToFitOnFirstNodes) {
      setTimeout(() => {
        fitView({
          duration: 500,
        });
      }, 200);
    }
  }, [previousLength, nodesLength, fitView, zoomToFitOnFirstNodes]);

  return (
    <SweepCanvas
      sweepGroups={sweepGroups}
      sweepNodes={sweepNodes}
      sweepEdges={sweepEdges}
      sweepStandaloneNodes={sweepStandaloneNodes}
      sweepSections={sweepSections}
      sweepStickyNotesNodes={sweepStickyNotesNodes}
      sweepTextNodes={sweepTextNodes}
      visibilityMap={visibilityMap}
      onNodeClick={onNodeClick}
      onPillClick={hasPillClick ? onPillClick : undefined}
      onLabelClick={hasLabelClick ? onLabelClick : undefined}
      onEdgeDeleteClick={_onRemoveFunnelLinkClick}
      readonly={readonly}
      holdNodeHighlighted={holdNodeHighlighted}
      hideGroupInfo={hideGroupInfo}
      autoFitViewOnFirstNodes={autoFitViewOnFirstNodes}
      isLoadingCursor={isLoadingCursor}
      showGroupOverlays={isPlacingPlugin}
      onPluginClick={onPluginClick}
      onSweepNodesChange={onSweepElementsChange}
      canvasNodesMouseMove={canvasNodesMouseMove}
      onConnectClick={_onConnectStepsClick}
      onGateClick={onGateClick}
      onNodeNameChange={onNodeNameChange}
      controlsRightMargin={controlsRightMargin}
      disableNodeHighlight={disableNodeHighlight}
      simpleHighlightedEntities={simpleHighlightedEntities}
      onlyRenderVisibleElements
      disableMultiSelection={disableMultiSelection}
    />
  );
};

const FunnelMapCanvasWithContext = (props: FunnelMapCanvasProps) => {
  return (
    <FunnelMapCanvasInternalCtx.Provider value={props}>
      <FunnelMapCanvas />
    </FunnelMapCanvasInternalCtx.Provider>
  );
};

export { FunnelMapCanvasWithContext as FunnelMapCanvas };
